import { Component, Inject, Prop, PropSync, Vue } from 'vue-property-decorator';
import { CustomerListEdit } from '@/shared/model/customer-list-edit.model';
import CustomDatePicker from '@/shared/components/custom-date-picker/custom-date-picker.vue';
import CustomerService from '@/customer/customer.service';
import { IStatus } from '@/shared/model/status.model';
import StatusService from '@/entities/status/status.service';
import ChanceWeightService from '@/entities/chance-weight/chance-weight.service';
import { IChanceWeight } from '@/shared/model/chanceWeight.model';
import { CustomerInterested } from '@/shared/model/enumerations/customer-interested.enum';
import { ISalesArea } from '@/shared/model/sales-area.model';
import SalesAreasService from '@/entities/sales-areas/sales-areas.service';
import AutoValidateDateField from '@/shared/components/auto-validate-date-field/auto-validate-date-field.vue';
import GroupManagementService from '@/admin/group-management/group-management.service';
import { PromptDialogContent } from '@/shared/model/prompt-dialog-content.model';
import { Alert, IAlert } from '@/shared/model/alert.model';
import { AlertType } from '@/shared/model/enumerations/alert-type.model';

@Component({
  components: {
    AutoValidateDateField,
    CustomDatePicker,
  },
})
export default class QuickAccess extends Vue {
  @PropSync('showDialog') dialog: boolean;
  @Inject('customerService') private customerService: () => CustomerService;
  @Inject('statusService') private statusService: () => StatusService;
  @Inject('chanceWeightService') private chanceWeightService: () => ChanceWeightService;
  @Inject('saleAreasService') private saleAreasService: () => SalesAreasService;
  @Inject('groupManagementService') private groupManagementService: () => GroupManagementService;

  @Prop() customerId: number;
  @Prop() inCalcRoute: boolean;

  private customerListEdit = new CustomerListEdit();
  private fetching = true;
  private saving = false;
  private form = false;
  private statuses: IStatus[] = [];
  private chanceWeights: IChanceWeight[] = [];
  private salesArea: ISalesArea[] = [];
  private showMarketerDialog = false;
  private addingNewMarketerOnCustomer = false;
  private externalMarketers = [];
  private selectedMarketers = [];
  private externalHeaders = [
    { text: 'Vorname', value: 'firstName' },
    { text: 'Nachname', value: 'lastName' },
    { text: 'E-Mail', value: 'email' },
    { text: 'hinzufügen', value: 'plus' },
  ];
  private customerInterested = [
    { text: 'sehr interessant', value: CustomerInterested.VERY_INTERESTED },
    { text: 'interessant', value: CustomerInterested.INTERESTED },
    { text: 'weniger interessant', value: CustomerInterested.LOW_INTERESTED },
    { text: 'passt nicht', value: CustomerInterested.NOT_INTERESTED },
  ];
  private modelIdent = ['A, C', 'TLV', 'WR', 'NB'];
  private rules = {
    withinOneYearRange: (value: any) => {
      if (!value) return true;

      const inputDate = new Date(value);
      const today = new Date();

      const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      const oneYearLater = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

      if (inputDate < oneYearAgo) {
        return 'Das Datum darf nicht mehr als ein Jahr in der Vergangenheit liegen.';
      }
      if (inputDate > oneYearLater) {
        return 'Das Datum darf nicht mehr als ein Jahr in der Zukunft liegen.';
      }

      return true;
    },
  };

  async mounted() {
    await this.retrieveStatuses();
    await this.retrieveSalesArea();
    await this.retrieveChanceWeights();
    await this.retrieveCustomerList();
    await this.getMarketers();
  }

  private async getMarketers() {
    Promise.all([
      this.groupManagementService().retrieveAllExternalMarketers(),
      this.customerService().retrieveAllCustomersMarketer(this.customerListEdit.id),
    ]).then(res => {
      this.externalMarketers = res[0];
      this.selectedMarketers = res[1];
    });
  }

  private showCustomerDetail() {
    this.$emit('showCustomerDetail', this.customerId);
  }

  private async addNewMarketerOnCustomer(marketerId: number) {
    this.addingNewMarketerOnCustomer = true;
    this.selectedMarketers = await this.customerService().addMarketersToCustomer(this.customerListEdit.id, marketerId);
    this.addingNewMarketerOnCustomer = false;
  }

  private async removeExternalMarketerFromCustomer(marketerId: number) {
    try {
      this.$store.commit(
        'appStore/setPromptDialogContent',
        new PromptDialogContent('Löschen bestätigen', 'Sind Sie sicher, Zugang zu diesem Makler zu löschen?', 'Löschen', 'Abbrechen')
      );
      await this.$store.dispatch('appStore/promptDialog');
      this.addingNewMarketerOnCustomer = true;
      await this.customerService().deleteAccess(this.customerListEdit.id, marketerId);

      this.selectedMarketers.map((item, index) => {
        if (item.id === marketerId) {
          this.selectedMarketers.splice(index, 1);
        }
      });
      this.addingNewMarketerOnCustomer = false;
      const message = 'Makler aus der Zugriffsliste entfernt';
      this.snackbarContent.push(new Alert(AlertType.SUCCESS, message));
    } catch (error) {
      if (error) {
        this.snackbarContent.push(new Alert(AlertType.ERROR, 'Es ist ein Fehler passiert'));
      }
    }
  }

  private get maxDate() {
    const today = new Date();
    const max = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    return max.toISOString().split('T')[0];
  }

  private get externalMarketersNames() {
    return this.customerListEdit?.externalMarketers?.join(', ');
  }

  private async retrieveCustomerList() {
    this.fetching = true;
    await this.customerService()
      .getCustomerListData(this.customerId)
      .then(response => {
        this.customerListEdit = response;
      })
      .catch(err => console.error(err))
      .finally(() => {
        this.fetching = false;
      });
  }

  private async retrieveStatuses() {
    await this.statusService()
      .retrieve()
      .then(response => {
        this.statuses = response;
      })
      .catch(err => console.error(err));
  }

  private async retrieveSalesArea() {
    await this.saleAreasService()
      .retrieve()
      .then(response => {
        this.salesArea = response.data;
      })
      .catch(err => console.error(err));
  }

  private async retrieveChanceWeights() {
    await this.chanceWeightService()
      .retrieve()
      .then(response => {
        this.chanceWeights = response.data;
      })
      .catch(err => console.error(err));
  }

  private openMarketerDialog() {
    this.showMarketerDialog = true;
  }

  private async save() {
    this.saving = true;
    await this.customerService()
      .updateCustomerList(this.customerListEdit)
      .then(response => {
        this.$emit('listUpdate', response);
        this.dialog = false;
      })
      .catch(err => console.error(err))
      .finally(() => {
        this.saving = false;
      });
  }

  private get snackbarContent(): IAlert[] {
    return this.$store.getters['appStore/getSnackbarContent'];
  }

  private set snackbarContent(newSnackbarContent: IAlert[]) {
    this.$store.dispatch('appStore/setSnackbarContent', newSnackbarContent).then(null);
  }
}
